import { ReferenceDataOptionLists } from '../../../lib/constants/reference-data-option-lists';

import { Address } from '../../../lib/models/api/address.model';
import { Deserializable } from '../../../lib/models/deserializable';
import { ApplicantDependant } from './applicant-dependant.model';
import { ApplicantEmployment } from './applicant-employment.model';
import { ApplicantIncomeFromOutsideUK } from './applicant-income-from-outside-uk.model';
import { ApplicantResidence } from './applicant-residence.model';
import { ApplicantSourceOfWealth } from './applicant-source-of-wealth.model';
import { ApplicantSponsorDependant } from './applicant-sponsor-dependant.model';
import { ExistingProperty } from './existing-property.model';
import { OutstandingDebt } from './outstanding-debt.model';

export class Applicant implements Deserializable {
  public ApplicantNumber: number;
  public ApplicationNumber?: number;
  public ArrangementWithCreditorCode: string;
  public ApplicantTransactOrder?: number;
  public BankruptCode: string;
  public BeenInArrearCode: string;
  public ConvertPropertyToBtlCode: string;
  public CourtJudgmentCode: string;
  public DateOfBirth?: Date;
  public EmailAddress: string;
  public Forename: string;
  public LenderAddressID?: number;
  public FutureCorrespondingAddressID?: number;
  public HomeTelephoneNumber: string;
  public HowLongWithLenderMonths?: number;
  public HowLongWithLenderYears?: number;
  public MaritalStatusCode: string;
  public MobileTelephoneNumber: string;
  public MortgageAccountNumber: string;
  public NationalityCode: string;
  public NationalInsuranceNumber: string;
  public NumberOfDependants?: number;
  public HowManyOtherNonBtl?: number;
  public PreviousForename: string;
  public UsedPreviousSurnameCode: string;
  public PreviousSurname: string;
  public PropertySellingPrice?: number;
  public RedeemingResidentialMortgageCode: string;
  public RefusedLoanCode: string;
  public ResidentialMortgageOwed?: number;
  public Surname: string;
  public TitleCode: string;
  public WorkTelephoneNumber: string;
  public HoldMortgageCode: string;
  public HoldAnyOtherNonBtlCode: string;
  public HoldMortgageWithin12LastMonthCode: string;
  public WhoLender: string;
  public ApproximateOutstandingBalance?: number;
  public EventsWhichAffectExpenditureCode: string;
  public DetailsOfChangeExpenditure: string;
  public ImpactOnExpenditure: string;
  public DateEffectOfExpenditure: string;
  public MiddleName: string;
  public GenderCode: string;
  public OfferDistributionChannel: string;
  public FirstTimeBuyerCode: string;
  public BirthCountryCode: string;
  public BeenInArrearDetails: string;
  public CourtJudgmentDetails: string;
  public BankruptDetails: string;
  public ArrangementWithCreditorDetails: string;
  public RefusedLoanDetails: string;
  public CustomerTypeCode: string;
  public HoldAnyBtlCode: string;
  public HowManyBtl?: number;
  public SellingCurrentResidentialPropertyCode: string;
  public BalanceOutstandingAndReasonForMortgageDetails: string;
  public WhoWasLender: string;
  public HoldMortgageBTLCode: string;
  public AmlSearchResultID?: number;
  public MonthlyRentalAmount?: number;
  public MonthlyMortgageAmount?: number;
  public MortgageRemainderFundingDetails: string;
  public IsPartToRemortgageCode: string;
  public AmountOwedOnRemortgagedProperty?: number;
  public IsLoanSponsorCode: string;
  public IsOnDeedsCode: string;
  public SolicitorAddressID?: number;
  public HadIntentionToRentToStrangersCode: string;
  public IsPropertyInheritedCode: string;
  public HaveYouLivedOnThisPropertyCode: string;
  public ApplicantIdentifier?: string;
  public HasMortgageAnERC: string;
  public ERCAmount?: number;
  public ERCEndDate?: Date;
  public IsPortableMortgage: string;
  public TotalMonthlyPayment?: number;
  public OutstandingBalanceAmount?: number;
  public HoldMortgageOnSponsorPropertyCode: string;
  public OutstandingBalanceOnSponsorProperty?: number;
  public MortgageYearsRemainingOnSponsorProperty?: number;
  public RepaymentStrategyOnSponsorPropertyCode: string;
  public MonthlyCostOnSponsorProperty?: number;
  public NumberOfSponsorDependants?: number;
  public MonthlyMortgageOnSponsorProperty?: number;
  public LengthOfUKResidencyCode: string;
  public HasIncomeFromOutsideUKCode: string;
  public KnowILASolicitorDetailsCode: string;
  public ApplicantExtendedID?: number;
  public RemortgageCurrentResidentialPropertyCode: string;
  public NewMonthlyMortgagePaymentPrediction?: number;
  public NewOutstandingBalancePrediction?: number;
  public FutureCorrespondingAddress: Address;
  public LenderAddress: Address;
  public SolicitorAddress: Address;
  public ApplicantDependants: Array<ApplicantDependant>;
  public ApplicantEmployments: Array<ApplicantEmployment>;
  public ApplicantIncomeFromOutsideUKs: Array<ApplicantIncomeFromOutsideUK>;
  public ApplicantResidences: Array<ApplicantResidence>;
  public ApplicantSourceOfWealths: Array<ApplicantSourceOfWealth>;
  public ApplicantSponsorDependants: Array<ApplicantSponsorDependant>;
  public ExistingProperties: Array<ExistingProperty>;
  public OutstandingDebts: Array<OutstandingDebt>;
  public CreationOrder: number;
  public Order: number;
  public IsAccountHolder: boolean;
  public FullName: string;

  public AgeAtMortgageTerm?: number;
  public TotalIncome = 0;

  constructor() {
    this.Forename = '';
    this.Surname = '';
    this.ApplicantResidences = [new ApplicantResidence()];
    this.ApplicantResidences[0].CurrentResidence = true;
    this.ApplicantResidences[0].ApplicantResidenceOrder = 0;
    this.ApplicantEmployments = [new ApplicantEmployment()];

    this.FutureCorrespondingAddress = new Address();
    this.LenderAddress = new Address();
    this.SolicitorAddress = new Address();

    this.ApplicantSourceOfWealths = new Array<ApplicantSourceOfWealth>();
    this.OutstandingDebts = new Array<OutstandingDebt>();
  }

  deserialize(input: any): this {
    Object.assign(this, input);
    this.FutureCorrespondingAddress = new Address().deserialize(input.FutureCorrespondingAddress);
    this.LenderAddress = new Address().deserialize(input.LenderAddress);
    this.SolicitorAddress = new Address().deserialize(input.SolicitorAddress);
    if (input.ApplicantDependants) {
      this.ApplicantDependants = input.ApplicantDependants.map((applicantDependant) =>
        new ApplicantDependant().deserialize(applicantDependant)
      );
    }
    if (input.ApplicantEmployments) {
      this.ApplicantEmployments = input.ApplicantEmployments.map((applicantEmployment) =>
        new ApplicantEmployment().deserialize(applicantEmployment)
      );
    }
    if (input.ApplicantIncomeFromOutsideUKs) {
      this.ApplicantIncomeFromOutsideUKs = input.ApplicantIncomeFromOutsideUKs.map((applicantIncomeFromOutsideUK) =>
        new ApplicantIncomeFromOutsideUK().deserialize(applicantIncomeFromOutsideUK)
      );
    }
    if (input.ApplicantSourceOfWealths) {
      this.ApplicantSourceOfWealths = input.ApplicantSourceOfWealths.map((applicantSourceOfWealth) =>
        new ApplicantSourceOfWealth().deserialize(applicantSourceOfWealth)
      );
    }
    if (input.ApplicantSponsorDependants) {
      this.ApplicantSponsorDependants = input.ApplicantSponsorDependants.map((applicantSponsorDependant) =>
        new ApplicantSponsorDependant().deserialize(applicantSponsorDependant)
      );
    }
    if (input.ExistingProperties) {
      this.ExistingProperties = input.ExistingProperties.map((existingProperty) =>
        new ExistingProperty().deserialize(existingProperty)
      );
    }
    if (input.OutstandingDebts) {
      this.OutstandingDebts = input.OutstandingDebts.map((outstandingDebt) =>
        new OutstandingDebt().deserialize(outstandingDebt)
      );
    }
    this.FullName = `${this.Forename} ${this.Surname}`;

    this.initializeApplicantResidences(input);

    return this;
  }

  isPartToRemortgage(): boolean {
    return this.IsPartToRemortgageCode === ReferenceDataOptionLists.AnswerTypeYes;
  }

  hadAPreviousSurname(): boolean {
    return this.UsedPreviousSurnameCode === ReferenceDataOptionLists.AnswerTypeYes;
  }

  canHoldMortgageOnSponsorProperty(): boolean {
    return this.IsLoanSponsorCode === ReferenceDataOptionLists.AnswerTypeYes;
  }

  cannotHoldMortgageOnSponsorProperty(): boolean {
    return this.IsLoanSponsorCode !== ReferenceDataOptionLists.AnswerTypeYes;
  }

  isHoldingMortgageOnSponsorProperty(): boolean {
    return (
      this.canHoldMortgageOnSponsorProperty() &&
      this.HoldMortgageOnSponsorPropertyCode === ReferenceDataOptionLists.AnswerTypeYes
    );
  }

  isOwnerOrPurchasingWithOwnOrOtherResidence(): boolean {
    // TODO: check if this condition should remain
    if (!this.ApplicantResidences) {
      return false;
    }

    const isResidenceOwnOrOther = this.ApplicantResidences.some(
      (residence) =>
        residence.ResidenceTypeCode === ReferenceDataOptionLists.ResidentialStatusOwn ||
        residence.ResidenceTypeCode === ReferenceDataOptionLists.ResidentialStatusOther
    );

    return this.isPurchasingOrMovingHouse() && isResidenceOwnOrOther;
  }

  holdsMortgage(): boolean {
    return this.HoldMortgageCode === ReferenceDataOptionLists.AnswerTypeYes;
  }

  ownsCurrentResidence(): boolean {
    return (
      this.ApplicantResidences &&
      this.ApplicantResidences.some(
        (residence) =>
          residence.CurrentResidence && residence.ResidenceTypeCode === ReferenceDataOptionLists.ResidentialStatusOwn
      )
    );
  }

  isPurchasingOrMovingHouse(): boolean {
    return (
      this.CustomerTypeCode === ReferenceDataOptionLists.CustomerTypeOwnerMovingHouse ||
      this.CustomerTypeCode === ReferenceDataOptionLists.CustomerTypePurchase
    );
  }

  isRemortgaging(): boolean {
    return this.CustomerTypeCode === ReferenceDataOptionLists.CustomerTypeRemortgaging;
  }

  private initializeApplicantResidences(input: any): void {
    if (input.ApplicantResidences && input.ApplicantResidences.length > 0) {
      this.ApplicantResidences = input.ApplicantResidences.map((applicantResidence) =>
        new ApplicantResidence().deserialize(applicantResidence)
      );
    } else {
      this.ApplicantResidences = new Array<ApplicantResidence>(new ApplicantResidence());
    }

    this.ApplicantResidences[0].CurrentResidence = true;
    this.ApplicantResidences[0].ApplicantResidenceOrder = 0;
  }
}
